import React, { useState } from "react";
import { Button, LoadingButton, TextButton } from "kubra-ux-forge";
import { useDispatch, useSelector } from "react-redux";
import { PageLoader } from "components/ezpay/common/page-loader";
import { getBiller, getLabels } from "selectors";
import { ContactAndTocFooter } from "components/ezpay/common/contact-and-toc-footer";
import { useTranslation } from "react-i18next";
import { PAYABLE_REVIEW_NAMESPACE } from "constants/i18n-namespaces";
import { useNavigate } from "react-router";
import { getPaymentIntent } from "selectors/paymentIntent";
import routing from "routing";
import { setPayableSession } from "actions/payableSessionActions";
import IamApi from "api/IamApi";
import { USER_LOGOUT } from "actions/userActions";
import { getPayableSession } from "selectors/payableSession";
import { getDeviceFingerprint } from "selectors/appState";
import { getDomain, getLabel } from "utilities/helper";
import { ACTION } from "actions/paymentIntentActions";
import { PayableCardHeader } from "components/ezpay/payables/payable-card-header";
import { PaymentSuccessSummary } from "../payment-success-summary";
import { ExtensionFieldValue } from "types/payable";

function getExtensionFieldValue(field: ExtensionFieldValue) {
  if (field.type === "PhoneNumber") {
    const phonePrefix = "tel:";
    return field.value.startsWith(phonePrefix)
      ? field.value.slice(phonePrefix.length)
      : field.value;
  } else if (field.type === "Email") {
    const emailPrefix = "mailto:";
    return field.value.startsWith(emailPrefix)
      ? field.value.slice(emailPrefix.length)
      : field.value;
  } else {
    return field.value;
  }
}

export const PaymentSuccess = () => {
  const { t, i18n } = useTranslation(PAYABLE_REVIEW_NAMESPACE);
  const [makeAnotherPaymentLoading, setMakeAnotherPaymentLoading] =
    useState("text");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const payableSession = useSelector(getPayableSession);
  const paymentIntentState = useSelector(getPaymentIntent);

  const deviceFingerprint = useSelector(getDeviceFingerprint);

  const biller = useSelector(getBiller);
  const labelSource = useSelector(getLabels);

  const handleMakeAnotherPayment = async () => {
    setMakeAnotherPaymentLoading("loading");
    try {
      const payableType = payableSession.payableType;
      const payable = payableSession.payable;

      if (!payable?.krn) {
        throw new Error("Payable KRN is unavailable in the session.");
      }

      await IamApi.ezpay.logout();

      // TODO: dispatching USER_LOGOUT wipes out all state in the redux store.
      // This causes few problems:
      // 1. forces refetching of the biller configuration (causing unnecessary re-renders.)
      // 2. the ProtectedRoute checks for a payable session and navigates to the index page, if it doesn't exist.
      // dispatch({ type: USER_LOGOUT });
      // dispatch(setPayableSession(undefined));

      // To be able to make a another payment for the same payable, we need to create
      // a new session (EZ-PAY API) by calling the validate payable endpoint and then
      // initiating a payment intent for the same payable.

      // Grab the first lookup from the payable type and get the field values from the payable.
      const lookup = payableType.lookups[0];
      const lookupFields: Record<string, string> = lookup.fields.reduce(
        (fieldValues, field) => ({
          ...fieldValues,
          [field]: payable.lookupFields[field]?.value ?? "",
        }),
        {}
      );

      // For account payables, the account number is not part of the lookup fields, so add it manually.
      if (payable.category === "account") {
        lookupFields["accountNumber"] = payable.accountNumber;
      }

      const domain = getDomain();
      const lookupRequest = {
        typeKrn: payableType.typeKrn,
        lookupFields: { ...lookupFields },
        domain: domain,
        channel: "Web",
        language: i18n.language,
      };

      await IamApi.ezpay.postLookups(biller.spaceKrn, lookupRequest);

      await IamApi.ezpay.postPayableInitiateIntent(
        payable.krn,
        deviceFingerprint,
        navigator.userAgent
      );

      const newPayableSession = await IamApi.ezpay.getPayableFromSession(
        domain
      );

      dispatch(setPayableSession(newPayableSession));
      dispatch({ type: ACTION.RESET_PAYMENT_INTENT_STATE });
      navigate(routing.payableDashboard.index());
    } catch (error) {
      setMakeAnotherPaymentLoading("text");
      console.error(
        "Error while trying to create session for making another payment",
        error
      );

      navigate(routing.index());
    }
  };

  const handlePayDifferentItem = async () => {
    await IamApi.ezpay.logout();
    dispatch({ type: USER_LOGOUT });
    navigate(routing.index());
  };

  return (
    <div className="ez-pay-balance-frame" data-cy="ez-pay-review-frame">
      <PageLoader />
      <div className="ez-pay-review-container" data-cy="ez-pay-review-top">
        <PayableCardHeader showBreadcrumb={false} />
        <div className="ez-pay-payment-success-top-row">
          <h2>{t("payment-completed")}</h2>
          {paymentIntentState?.receiptInfo?.email === "" &&
          paymentIntentState.receiptInfo?.mobileNumber === "" ? null : (
            <p>
              {t("receipt-info")}{" "}
              <b>{paymentIntentState?.receiptInfo?.email}</b>
              {paymentIntentState?.receiptInfo?.mobileNumber && (
                <>
                  {" "}
                  {t("and")}{" "}
                  <b>{paymentIntentState.receiptInfo.mobileNumber}</b>
                </>
              )}
            </p>
          )}
        </div>
        {payableSession?.payable?.extensionFields &&
        Object.keys(payableSession.payable.extensionFields).length > 0 ? (
          <div
            className="ez-pay-payment-top-row"
            data-cy="ez-pay-review-top-row"
          >
            {Object.entries(payableSession.payable.extensionFields || {}).map(
              ([fieldKey, field]) => (
                <div
                  key={fieldKey}
                  className="ez-pay-extension-field"
                  data-cy={`ez-pay-review-extension-field-${fieldKey}`}
                >
                  <div
                    className="ez-pay-extension-field-label"
                    data-cy={`ez-pay-review-extension-label-${fieldKey}`}
                  >
                    {getLabel(labelSource.labels, fieldKey)}:
                  </div>{" "}
                  {getExtensionFieldValue(field)}
                </div>
              )
            )}
          </div>
        ) : (
          <hr className="ez-pay-review-separator" />
        )}
        <div className="ez-pay-review-content" data-cy="ez-pay-review-content">
          <PaymentSuccessSummary />

          <div className="ez-pay-review-buttons-container">
            {biller.payableTypeDefinitions.length > 1 ? (
              <div>
                <TextButton onClick={handlePayDifferentItem}>
                  {t("pay-different-item")}
                </TextButton>
              </div>
            ) : null}
            <div>
              <LoadingButton
                type="button"
                onClick={handleMakeAnotherPayment}
                loadingState={makeAnotherPaymentLoading}
              >
                {t("make-another-payment")}
              </LoadingButton>
            </div>
          </div>
          <div className="ez-pay-payable-row">
            <ContactAndTocFooter biller={biller} />
          </div>
        </div>
      </div>
    </div>
  );
};
